import React, {useState, useEffect} from 'react';
import {Box, VStack, HStack, Switch, useTheme, Flex} from '@chakra-ui/react';
import {TextG12, TextG14, TextG16, TextG24, TextG32} from '../../components/typography';
import Icon from '../../components/Icon';
import * as pushService from '../../pushService';
import {isIphone} from '../../utils/deviceCheck';
import useAuth from '../../hooks/useAuth';

interface IntroNotificationsProps {
  onValidationChange: (isValid: boolean) => void;
  onEmailEnabledChange: (enabled: boolean) => void;
  onSmsEnabledChange: (enabled: boolean) => void;
  onPushEnabledChange: (enabled: boolean) => void;
}

const IntroNotifications = ({
  onValidationChange,
  onEmailEnabledChange,
  onSmsEnabledChange,
  onPushEnabledChange,
}: IntroNotificationsProps) => {
  const theme = useTheme();
  const {user} = useAuth();
  const [emailEnabled, setEmailEnabled] = useState(user?.emailEnabled ?? true);
  const [smsEnabled, setSmsEnabled] = useState(user?.smsEnabled ?? false);
  const [pushEnabled, setPushEnabled] = useState(user?.pushEnabled ?? true);

  // This step is always valid (user can skip it)
  useEffect(() => {
    onValidationChange(true);
  }, [onValidationChange]);

  // Notify parent of changes
  useEffect(() => {
    onEmailEnabledChange(emailEnabled);
  }, [emailEnabled, onEmailEnabledChange]);

  useEffect(() => {
    onSmsEnabledChange(smsEnabled);
  }, [smsEnabled, onSmsEnabledChange]);

  useEffect(() => {
    onPushEnabledChange(pushEnabled);
  }, [pushEnabled, onPushEnabledChange]);

  const handlePushToggle = () => {
    const newValue = !pushEnabled;
    setPushEnabled(newValue);

    if (newValue) {
      // Request push notification permission
      if (window['ReactNativeWebView']) {
        pushService.pushPermissionRequest();
      } else {
        if (!isIphone) {
          pushService.askNotificationPermission().catch((error) => {
            console.error('Failed to request push notification permission:', error);
            // Revert the toggle if permission was denied
            setPushEnabled(false);
          });
        } else {
          pushService.pushPermissionRequest();
        }
      }
    }
  };

  return (
    <VStack
      flex={1}
      spacing={6}
      px={4}
      pt={4}
      pb={4}
      align="stretch"
      justify="flex-start"
      overflow="auto"
      bg="#F8F6F7">
      {/* Title */}
      <Box textAlign="center">
        <Flex flexDir="column" align="center" gap={1}>
          <TextG32 fontSize="32px" fontWeight="700" lineHeight="44px" color="#262626">
            🔔
          </TextG32>
          <TextG32 fontSize="32px" fontWeight="700" lineHeight="44px" color="#262626">
            Don’t miss out!
          </TextG32>
        </Flex>
      </Box>

      {/* Description */}
      <TextG16 textAlign="center" lineHeight="24px" color="#636363" fontWeight="500">
        Enable notifications and alerts to <strong>know when neighbors are connecting</strong> and
        not miss <strong>limited opportunities</strong>.
      </TextG16>

      {/* Notification Options - Combined in one box */}
      <Box
        bg="#FFF"
        borderRadius="32px"
        p="24px"
        mt="24px"
        boxShadow="0 2px 10px 0 rgba(201, 19, 107, 0.03)">
        <VStack spacing={4} align="stretch">
          {/* Push Notifications */}
          <HStack spacing={4} align="center">
            <Box
              bg={pushEnabled ? '#D1F4D0' : 'rgba(99, 99, 99, 0.15)'}
              borderRadius="12px"
              w="48px"
              h="48px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}>
              <Icon
                iconName="fi-rr-bell"
                style={{
                  fontSize: '20px',
                  color: pushEnabled ? '#048400' : '#636363',
                }}
              />
            </Box>
            <VStack align="flex-start" spacing={0} flex={1}>
              <TextG14 fontWeight="700" color="#262626">
                Turn on push notifications
              </TextG14>
              <TextG12 fontWeight="700" color={pushEnabled ? '#00A651' : '#636363'}>
                {pushEnabled ? 'On!' : 'Off'}
              </TextG12>
            </VStack>
            <Switch
              size="lg"
              colorScheme="green"
              isChecked={pushEnabled}
              onChange={handlePushToggle}
            />
          </HStack>

          {/* SMS Alerts */}
          <HStack spacing={4} align="center">
            <Box
              bg={smsEnabled ? '#D1F4D0' : 'rgba(99, 99, 99, 0.15)'}
              borderRadius="12px"
              w="48px"
              h="48px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}>
              <Icon
                iconName="fi-rr-mobile-button"
                style={{
                  fontSize: '20px',
                  color: smsEnabled ? '#048400' : '#636363',
                }}
              />
            </Box>
            <VStack align="flex-start" spacing={0} flex={1}>
              <TextG14 fontWeight="700" color="#262626">
                Turn on SMS alerts
              </TextG14>
              <TextG12 fontWeight="700" color={smsEnabled ? '#00A651' : '#636363'}>
                {smsEnabled ? 'On!' : 'Off'}
              </TextG12>
            </VStack>
            <Switch
              size="lg"
              colorScheme="green"
              isChecked={smsEnabled}
              onChange={() => setSmsEnabled(!smsEnabled)}
            />
          </HStack>

          {/* Privacy Note */}
          <Box pt={2}>
            <TextG14 textAlign="center" lineHeight="22px" color="#636363" fontWeight="500">
              Walkabout will only send community content. We do not sell ads.
            </TextG14>
          </Box>
        </VStack>
      </Box>
    </VStack>
  );
};

export default IntroNotifications;
